// src/pages/finance-portal/executive-dashboard/ExecutiveDashboard.js
//
// Executive Financial Dashboard
//
// Single-screen CFO landing — KPIs aggregated from existing finance tables:
// revenue / expense / net income / cash runway / AR / AP / active budget.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Card, Skeleton, Alert, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

function fmtMoney(val, opts = {}) {
    if (val == null || isNaN(val)) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD',
        maximumFractionDigits: opts.decimals ?? 0,
    }).format(val);
}

function KpiCard({ label, value, sublabel, color = '#0f172a', icon }) {
    return (
        <Card sx={{ borderRadius: '14px', p: 2.5, boxShadow: 'none',
                    border: '1px solid #e2e8f0', minHeight: 120,
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b' }}>
                {icon}
                <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600,
                                  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                </Typography>
            </Box>
            <Box>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color }}>
                    {value}
                </Typography>
                {sublabel && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                        {sublabel}
                    </Typography>
                )}
            </Box>
        </Card>
    );
}

export default function ExecutiveDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/executive-dashboard');
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <InsightsIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Executive Dashboard
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                                {data?.as_of && `As of ${data.as_of}`}
                            </Typography>
                        </Box>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} sx={{ color: 'white',
                                                              bgcolor: 'rgba(255,255,255,0.15)',
                                                              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {loading ? (
                    <Box>
                        <Skeleton height={140} sx={{ mb: 2 }} />
                        <Skeleton height={140} sx={{ mb: 2 }} />
                        <Skeleton height={300} />
                    </Box>
                ) : !data ? null : (
                    <>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem',
                                           color: '#64748b', textTransform: 'uppercase',
                                           letterSpacing: '0.08em', mb: 1 }}>
                            Revenue & Profitability
                        </Typography>
                        <Box sx={{ display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                  gap: 2, mb: 3 }}>
                            <KpiCard label="Revenue MTD" value={fmtMoney(data.revenue.mtd)} color="#16a34a"
                                     icon={<TrendingUpIcon fontSize="small" />} />
                            <KpiCard label="Revenue QTD" value={fmtMoney(data.revenue.qtd)} color="#16a34a"
                                     icon={<TrendingUpIcon fontSize="small" />} />
                            <KpiCard label="Revenue YTD" value={fmtMoney(data.revenue.ytd)} color="#15803d"
                                     icon={<TrendingUpIcon fontSize="small" />} />
                            <KpiCard label="Net Income YTD"
                                     value={fmtMoney(data.profitability.net_income_ytd)}
                                     color={data.profitability.net_income_ytd >= 0 ? '#16a34a' : '#ef4444'}
                                     sublabel={data.profitability.gross_margin_pct != null
                                         ? `Margin: ${data.profitability.gross_margin_pct.toFixed(1)}%` : null}
                                     icon={data.profitability.net_income_ytd >= 0
                                         ? <TrendingUpIcon fontSize="small" />
                                         : <TrendingDownIcon fontSize="small" />} />
                        </Box>

                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem',
                                           color: '#64748b', textTransform: 'uppercase',
                                           letterSpacing: '0.08em', mb: 1 }}>
                            Expenses & Cash
                        </Typography>
                        <Box sx={{ display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                  gap: 2, mb: 3 }}>
                            <KpiCard label="Expense MTD" value={fmtMoney(data.expense.mtd)} color="#dc2626"
                                     icon={<TrendingDownIcon fontSize="small" />} />
                            <KpiCard label="Expense YTD" value={fmtMoney(data.expense.ytd)} color="#b91c1c"
                                     icon={<TrendingDownIcon fontSize="small" />} />
                            <KpiCard label="Cash Balance" value={fmtMoney(data.cash.balance)} color="#0ea5e9"
                                     sublabel={`Burn: ${fmtMoney(data.cash.monthly_burn)}/mo`}
                                     icon={<AccountBalanceWalletIcon fontSize="small" />} />
                            <KpiCard label="Runway"
                                     value={data.cash.runway_months != null
                                         ? `${data.cash.runway_months.toFixed(1)} mo`
                                         : '—'}
                                     color="#0284c7"
                                     sublabel={data.cash.runway_months == null ? 'No burn detected' : null}
                                     icon={<AccountBalanceWalletIcon fontSize="small" />} />
                        </Box>

                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem',
                                           color: '#64748b', textTransform: 'uppercase',
                                           letterSpacing: '0.08em', mb: 1 }}>
                            Receivables & Payables
                        </Typography>
                        <Box sx={{ display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                  gap: 2, mb: 3 }}>
                            <Card sx={{ borderRadius: '14px', boxShadow: 'none',
                                        border: '1px solid #e2e8f0' }}>
                                <Box sx={{ p: 2 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                       display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ReceiptLongIcon sx={{ color: '#22c55e' }} fontSize="small" />
                                        Accounts Receivable
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem',
                                                              color: '#94a3b8', textTransform: 'uppercase' }}>
                                                Outstanding
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                                                {fmtMoney(data.accounts_receivable.outstanding)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem',
                                                              color: '#94a3b8', textTransform: 'uppercase' }}>
                                                Overdue
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem',
                                                              color: data.accounts_receivable.overdue > 0 ? '#ef4444' : '#1e293b' }}>
                                                {fmtMoney(data.accounts_receivable.overdue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={labelCell}>Top 5 Open Invoices</TableCell>
                                                <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Amount</TableCell>
                                                <TableCell sx={labelCell}>Due</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.accounts_receivable.top_5_overdue.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, ...dataCell, color: '#94a3b8' }}>
                                                        No open invoices
                                                    </TableCell>
                                                </TableRow>
                                            ) : data.accounts_receivable.top_5_overdue.map(i => (
                                                <TableRow key={i.id} hover sx={{ cursor: 'pointer' }}
                                                          onClick={() => navigate(`/finance-portal/invoices/${i.id}`)}>
                                                    <TableCell sx={dataCell}>{i.invoice_number || `#${i.id}`}</TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right', fontWeight: 600 }}>
                                                        {fmtMoney(i.amount)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                        {i.due_date}{i.overdue && (
                                                            <Chip label="Overdue" size="small"
                                                                  sx={{ ml: 0.5, fontFamily, fontSize: '0.65rem',
                                                                        height: 18, bgcolor: '#fecaca', color: '#991b1b' }} />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>

                            <Card sx={{ borderRadius: '14px', boxShadow: 'none',
                                        border: '1px solid #e2e8f0' }}>
                                <Box sx={{ p: 2 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                       display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ReceiptLongIcon sx={{ color: '#f59e0b' }} fontSize="small" />
                                        Accounts Payable
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem',
                                                              color: '#94a3b8', textTransform: 'uppercase' }}>
                                                Outstanding
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                                                {fmtMoney(data.accounts_payable.outstanding)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem',
                                                              color: '#94a3b8', textTransform: 'uppercase' }}>
                                                Overdue
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem',
                                                              color: data.accounts_payable.overdue > 0 ? '#ef4444' : '#1e293b' }}>
                                                {fmtMoney(data.accounts_payable.overdue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={labelCell}>Top 5 Open Bills</TableCell>
                                                <TableCell sx={{ ...labelCell, textAlign: 'right' }}>Amount</TableCell>
                                                <TableCell sx={labelCell}>Due</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.accounts_payable.top_5_overdue.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, ...dataCell, color: '#94a3b8' }}>
                                                        No open bills
                                                    </TableCell>
                                                </TableRow>
                                            ) : data.accounts_payable.top_5_overdue.map(b => (
                                                <TableRow key={b.id} hover sx={{ cursor: 'pointer' }}
                                                          onClick={() => navigate(`/finance-portal/bills/${b.id}`)}>
                                                    <TableCell sx={dataCell}>{b.bill_number || `#${b.id}`}</TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right', fontWeight: 600 }}>
                                                        {fmtMoney(b.amount)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                        {b.due_date}{b.overdue && (
                                                            <Chip label="Overdue" size="small"
                                                                  sx={{ ml: 0.5, fontFamily, fontSize: '0.65rem',
                                                                        height: 18, bgcolor: '#fecaca', color: '#991b1b' }} />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        </Box>

                        {data.active_budget && (
                            <Card sx={{ borderRadius: '14px', boxShadow: 'none',
                                        border: '1px solid #e2e8f0', p: 2.5,
                                        cursor: 'pointer' }}
                                  onClick={() => navigate(`/finance-portal/budgets/${data.active_budget.budget_id}/variance`)}>
                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b',
                                                  textTransform: 'uppercase', letterSpacing: '0.05em',
                                                  fontWeight: 600 }}>
                                    Active Budget — Click for Variance
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                          alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>
                                            {data.active_budget.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            FY {data.active_budget.fiscal_year} · {data.active_budget.period_start} → {data.active_budget.period_end}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.4rem', color: '#0f172a' }}>
                                        {fmtMoney(data.active_budget.budget_total)}
                                    </Typography>
                                </Box>
                            </Card>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
